import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService } from '../../services/navigation/navigation.service';
import { CacheService } from '../../services/cache/cache.service';
import { CommonService } from '../../services/common/common.service';
import { Column } from '../interfaces/column.interface';
import moment from 'moment';
import { FitnessSessionsDataService } from './fitness-sessions-data.service';
import { FitnessSession, FitnessSessionsResult } from '../interfaces';
import { MembersDataService } from './members-data.service';
import { Member, MembersResult } from '../interfaces';
import { UsersDataService } from './users-data.service';
import { User, UsersResult } from '../interfaces';

@Injectable({
  providedIn: 'root'
})
export class FitnessReservationsColumnsService {

  public fitnessSessions: FitnessSession[] = [];
  public members: Member[] = [];
  public users: User[] = [];
  public defaultParameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private cache: CacheService,
    private common: CommonService,
    private fitnessSessionsDataService: FitnessSessionsDataService,
    private membersDataService: MembersDataService,
    private usersDataService: UsersDataService,

  ) {
    this.getFitnessSessions()
    this.getMembers()
    this.getUsers()
  }

  getFitnessSessions(offset: number = 0): void {
    const cacheKey: string = 'fitnessSessions.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null && offset === 0) {
      this.fitnessSessions = cache.results
      return;
    }
    const parameters = Object.assign(this.defaultParameters, {offset: offset + ''});
    this.fitnessSessionsDataService.getFitnessSessions(parameters).subscribe((response: FitnessSessionsResult|any) => {
      if (response !== null) {
        response.results.map((item: any) => {
          item.label_ = '' + this.format('.startTime', item.startTime)  + ' : ' + this.format('.fitnessClass.name', item.fitnessClass.name)  + ' with ' + this.format('.coach.name', item.coach.name) ;
        });
        if (offset === 0) {
          this.fitnessSessions = response.results;
        } else {
          this.fitnessSessions = this.fitnessSessions.concat(response.results);
        }
        if (response.inlineCount > this.fitnessSessions.length) {
          offset = offset + parseInt(this.defaultParameters.limit + '', 10);
          this.getFitnessSessions(offset);
        }
        response.results = this.fitnessSessions;
        this.cache.set(cacheKey, response);
      }
    });
  }

  getMembers(offset: number = 0): void {
    const cacheKey: string = 'members.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null && offset === 0) {
      this.members = cache.results
      return;
    }
    const parameters = Object.assign(this.defaultParameters, {offset: offset + ''});
    this.membersDataService.getMembers(parameters).subscribe((response: MembersResult|any) => {
      if (response !== null) {
        if (offset === 0) {
          this.members = response.results;
        } else {
          this.members = this.members.concat(response.results);
        }
        if (response.inlineCount > this.members.length) {
          offset = offset + parseInt(this.defaultParameters.limit + '', 10);
          this.getMembers(offset);
        }
        response.results = this.members;
        this.cache.set(cacheKey, response);
      }
    });
  }

  getUsers(offset: number = 0): void {
    const cacheKey: string = 'users.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null && offset === 0) {
      this.users = cache.results
      return;
    }
    const parameters = Object.assign(this.defaultParameters, {offset: offset + ''});
    this.usersDataService.getUsers(parameters).subscribe((response: UsersResult|any) => {
      if (response !== null) {
        if (offset === 0) {
          this.users = response.results;
        } else {
          this.users = this.users.concat(response.results);
        }
        if (response.inlineCount > this.users.length) {
          offset = offset + parseInt(this.defaultParameters.limit + '', 10);
          this.getUsers(offset);
        }
        response.results = this.users;
        this.cache.set(cacheKey, response);
      }
    });
  }

  getColumns(): Column[] {
    return [
      { field: 'id', sortField: 'id', title: this.translate.instant('ID'), show: (this.navigation.getParamValue('fitnessReservations.id.showFiled', true) && true), displayInList: true, type: 'numericValue'},
      { field: 'attendingTime', sortField: 'attendingTime', title: this.translate.instant('ATTENDINGTIME'), show: (this.navigation.getParamValue('fitnessReservations.attendingTime.showFiled', true) && true), displayInList: true, type: 'dateValue', valueFormatter: this.translate.instant('DATETIMEFORMAT')},
      { field: 'attendingDuration', sortField: 'attendingDuration', title: this.translate.instant('ATTENDINGDURATION'), show: (this.navigation.getParamValue('fitnessReservations.attendingDuration.showFiled', true) && true), displayInList: true, type: 'numericValue'},
      { field: 'fitnessSession', title: this.translate.instant('FITNESSSESSION'), type: 'linkValue', filterData: this.fitnessSessions, show: (this.navigation.getParamValue('fitnessReservations.fitnessSessionId.showFiled', true) && true), displayInList: true, filterField: 'fitnessSession.id', displayField: 'label_', sortField: 'fitnessSession.label_', url: 'fitness-sessions' },
      { field: 'member', title: this.translate.instant('MEMBER'), type: 'linkValue', filterData: this.members, show: (this.navigation.getParamValue('fitnessReservations.memberId.showFiled', true) && true), displayInList: true, filterField: 'member.id', displayField: 'name', sortField: 'member.name', url: 'members' },
      { field: 'isConfirmed', sortField: 'isConfirmed', title: this.translate.instant('ISCONFIRMED'), show: (this.navigation.getParamValue('fitnessReservations.isConfirmed.showFiled', true) && true), displayInList: true, type: 'booleanValue', filterData: this.navigation.booleanOptions },
      { field: 'createdAt', sortField: 'createdAt', title: this.translate.instant('CREATEDAT'), show: (this.navigation.getParamValue('fitnessReservations.createdAt.showFiled', true) && true), displayInList: true, type: 'dateValue', valueFormatter: this.translate.instant('DATETIMEFORMAT')},
      { field: 'creatorUser', title: this.translate.instant('CREATORUSER'), type: 'linkValue', filterData: this.users, show: (this.navigation.getParamValue('fitnessReservations.creatorUser.showFiled', false) && true), displayInList: true, filterField: 'creatorUser.id', displayField: 'username', sortField: 'creatorUser.username', url: 'users' },
      { field: 'modifiedAt', sortField: 'modifiedAt', title: this.translate.instant('MODIFIEDAT'), show: (this.navigation.getParamValue('fitnessReservations.modifiedAt.showFiled', false) && true), displayInList: true, type: 'dateValue', valueFormatter: this.translate.instant('DATETIMEFORMAT')},
      { field: 'modifierUser', title: this.translate.instant('MODIFIERUSER'), type: 'linkValue', filterData: this.users, show: (this.navigation.getParamValue('fitnessReservations.modifierUser.showFiled', false) && true), displayInList: true, filterField: 'modifierUser.id', displayField: 'username', sortField: 'modifierUser.username', url: 'users' },
      { field: 'actions', title: this.translate.instant('ACTIONS'), type: 'static', show: true, displayInList: true, sortField: null},
    ];
  }

  format(fieldName: string, value: any) {
    if (fieldName.toLowerCase().indexOf('time') > -1) {
      return moment(value).format('YYYY-MM-DD HH:mm');
    } else if (fieldName.toLowerCase().indexOf('date') > -1) {
      return moment(value).format('YYYY-MM-DD');
    }
    return value;
  }

}
